import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaInfoCircle } from "react-icons/fa";
import { Row, Col, Spinner, Button, Toast, ToastContainer } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
// import SearchWithAPI from "../common/searchbox";

import Back from "../../assets/svg/back.svg";
import Delete from "../../assets/svg/delete-po.svg";
import SearchWithAPI from "../common/searchbox";
import { apiCall } from "../service/api-service";

import "../order/order.css";
import {
  createInvoiceApi,
  updateInvoiceApi,
  getOrderDetailsApi,
  getEditInvoiceDetailsApi,
  getReturnedProductApi,
} from "./invoice-api";
import { getOrgCurrencyApi, getCustomerDetailsApi } from "../order/order-api";
import CommonDropdown from "../common/common-new-dropdown.jsx";
import SearchWithFreeTextAPI from "../common/searchbox-free-text";
import { applyStateTaxFlags } from "../common/state-tax";
import {
  sanitizeInput,
  sanitizeNumericInput,
  sanitizeTextarea,
} from "../common/security-utils";

const MAX_NUMERIC_VALUE = 999999999;

const multiselectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    border: state.isFocused ? "1px solid #3d4af5" : "1px solid #e5e5e5",
    padding: "2px",
    fontWeight: "400",
    fontSize: "14px",
    color: "#17284d",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(61, 74, 245, 0.1)" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#3d4af5" : "#e5e5e5",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#3d4af5"
      : state.isFocused
        ? "#f8f9fb"
        : "#fff",
    color: state.isSelected ? "#fff" : "#17284d",
    padding: "10px 16px",
    fontSize: "14px",
    cursor: "pointer",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#EEEFFF",
    color: "#5D5FEF",
    borderRadius: "6px",
    padding: "2px 4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#5D5FEF",
    fontWeight: "500",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#5D5FEF",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#e0e2ff",
      color: "#3d4af5",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    maxHeight: "80px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#e5e5e5",
      borderRadius: "10px",
    },
  }),
  input: (provided) => ({
    ...provided,
    "& input": {
      boxShadow: "none !important",
      outline: "none !important",
      border: "none !important",
    },
  }),
};

const ErrorMessage = ({ message }) => {
  if (!message) return null;
  return (
    <span
      style={{
        color: "#ff4444",
        fontSize: "12px",
        fontWeight: "400",
        display: "block",
        marginTop: "4px",
      }}
    >
      {message}
    </span>
  );
};

// ⓘ icon with a breakdown tooltip rendered in a body portal so it is never
// clipped by the table/card overflow. `rows` is an array of [label, value,
// isTotal?] tuples shown above the icon.
const InfoTooltip = ({ rows }) => {
  const iconRef = useRef(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const openTooltip = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({ top: rect.top - 8, left: rect.right });
      setShow(true);
    }
  };
  const closeTooltip = () => setShow(false);

  return (
    <span
      ref={iconRef}
      className="total-info-wrap"
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
    >
      <FaInfoCircle className="total-info-icon" size={13} />
      {show &&
        createPortal(
          <span
            className="total-info-tooltip"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {rows.map(([label, value, isTotal], i) => (
              <span key={i} className={`tt-row${isTotal ? " tt-total" : ""}`}>
                <span>{label}</span>
                <span>{value}</span>
              </span>
            ))}
          </span>,
          document.body,
        )}
    </span>
  );
};

const InvoiceCreate = () => {
  const apiAccUrl = process.env.REACT_APP_ADMIN_API_URL;
  const orderUrl = process.env.REACT_APP_ORDER_API_URL;
  const apiUrl = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  const [invoiceId, setInvoiceId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Invoice creation mode — "order" (default, existing behavior) sources
  // everything from a Sales Order; "standalone" creates an invoice directly
  // against a customer with no underlying order. Only ever set on the create
  // (not edit) screen — edits keep whatever mode the invoice was created in,
  // driven off whether the loaded invoice has a sale_order_id.
  const [invoiceMode, setInvoiceMode] = useState("order");
  const [standaloneItemType, setStandaloneItemType] = useState("Product"); // Product or Service
  // The editable Product/Service item table (with Add Item) is shown for
  // standalone invoices on both create and edit. Billing-address inputs stay
  // create-only — editing an existing standalone invoice's address isn't
  // supported by this flow, it keeps whatever address it was created with.
  const isStandaloneItems = invoiceMode === "standalone";
  const isStandaloneCreate = invoiceMode === "standalone" && invoiceId == null;

  // Invoice Details
  const [salesOrderOption, setSalesOrderOption] = useState([]);
  const [salesOrderOptions, setSalesOrderOptions] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [customerOption, setCustomerOption] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [currencyOption, setCurrencyOption] = useState(null);

    const handleCurrencyDropdownChange = (option) => {
      setCurrencyOption(option);
    };

  // Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
  const decodeHtmlEntity = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  // Symbol of the selected currency; falls back to ₹ when none is selected.
  const currencySymbol = currencyOption?.symbol
    ? decodeHtmlEntity(currencyOption.symbol)
    : "₹";
  const [invoiceStatus, setInvoiceStatus] = useState("Sent");

  const [exchangeRate, setExchangeRate] = useState("0");

  // Organization's own currency — the Conversion Rate field is only shown
  // when the selected currency differs from this. Name/symbol are used to
  // show the "Total in {org currency}" row below the Total.
  const [orgCurrencyId, setOrgCurrencyId] = useState(null);
  const [orgCurrencyName, setOrgCurrencyName] = useState("");
  const [orgCurrencySymbol, setOrgCurrencySymbol] = useState("");
  const showConversionRate =
    orgCurrencyId != null &&
    currencyOption?.value != null &&
    String(orgCurrencyId) !== String(currencyOption.value);
  // Customer Details
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");

  // Billing address breakdown — only populated/used in standalone mode (the
  // order-based flow derives billing address server-side from the selected
  // Sales Order's SalesOrderAddress, same as before this feature existed).
  const [billingCountryOption, setBillingCountryOption] = useState(null);
  const [billingStateOption, setBillingStateOption] = useState(null);
  const [billingCityOption, setBillingCityOption] = useState(null);
  const [billingZipCode, setBillingZipCode] = useState("");

  const handleBillingCountryDropdownChange = (option) => {
    setBillingCountryOption(option);
    setBillingStateOption(null);
    setBillingCityOption(null);
  };

  const handleBillingStateDropdownChange = (option) => {
    setBillingStateOption(option);
    setBillingCountryOption({
      label: option.country_name,
      value: option.country_id,
    });
    setBillingCityOption(null);
  };

  const handleBillingCityDropdownChange = (option) => {
    setBillingCityOption(option);
    setBillingStateOption({ label: option.state_name, value: option.state_id });
    setBillingCountryOption({
      label: option.country_name,
      value: option.country_id,
    });
  };

  // Shipping Address
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingCountryOption, setShippingCountryOption] = useState(null);
  const [shippingStateOption, setShippingStateOption] = useState(null);
  const [shippingCityOption, setShippingCityOption] = useState(null);
  const [shippingZipCode, setShippingZipCode] = useState("");

  // Terms & Conditions and Notes
  const [termsConditions, setTermsConditions] = useState("");
  const [notes, setNotes] = useState("");

  // Products
  const [items, setItems] = useState([]);

  // Removed Orders
  const [removedOrders, setRemovedOrders] = useState([]);

  // Returned Products
  const [returnedItems, setReturnedItems] = useState([]);

  // Charges
  const [charges, setCharges] = useState([]);

  // Discount
  const [discounts, setDiscounts] = useState([]);

  // Totals
  const [subTotal, setSubTotal] = useState(0);
  const [subTotalWithOutTax, setSubTotalWithOutTax] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("Rs.");
  const [tdsOption, setTdsOption] = useState("TDS");
  const [tdsPercentage, setTdsPercentage] = useState("0");
  const [discountTotal, setDiscountTotal] = useState(0);
  const [adjustment, setAdjustment] = useState(0);
  const [total, setTotal] = useState(0);
  const [tdsCalcValue, setTdsCalcValue] = useState(0);

  const [returnedSubTotal, setReturnedSubTotal] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [gst, setGst] = useState(0);
  const [igst, setIgst] = useState(0);
  const [totalTax, setTotalTax] = useState(0);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const [validations, setValidations] = useState({});

  // Selected items for checkboxes
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedReturnedItems, setSelectedReturnedItems] = useState([]);

  const isValidId = (value) => {
    if (value === null || value === undefined) return false;
    return /^[a-zA-Z0-9_-]+$/.test(String(value));
  };

  const sanitizeNumber = (value, max = MAX_NUMERIC_VALUE) =>
    sanitizeNumericInput(value, max);

  const sanitizeSignedNumericInput = (input, max = MAX_NUMERIC_VALUE) => {
    if (input === null || input === undefined) return "";

    const raw = String(input);
    const cleaned = raw.replace(/[^0-9.-]/g, "").replace(/(?!^)-/g, "");

    const [head, ...tail] = cleaned.split(".");
    const normalized = tail.length > 0 ? `${head}.${tail.join("")}` : head;

    if (
      normalized === "" ||
      normalized === "-" ||
      normalized === "." ||
      normalized === "-."
    ) {
      return normalized;
    }

    // If input is still being typed (ends with . or incomplete), return as-is
    if (normalized.endsWith(".")) {
      return normalized;
    }

    const parsed = Number.parseFloat(normalized);
    if (Number.isNaN(parsed)) return "";

    const clamped = Math.max(-max, Math.min(parsed, max));
    return clamped.toString();
  };

  useEffect(() => {
    if (id != null) {
      if (!isValidId(id)) {
        setSnackbarMessage("Invalid invoice id");
        setSnackbarStatus(false);
        handleSnackbarShow();
        navigate(-1);
        return;
      }
      getInvoiceDetails();
      return;
    }

    const today = new Date().toISOString().slice(0, 10);
    setInvoiceDate(today);
    setDueDate(today);
  }, [id]);
  const getInvoiceDetails = async () => {
    try {
      const response = await getEditInvoiceDetailsApi(id);
      if (response?.data.success === true && response?.data?.invoice != null) {
        const invoice = response.data.invoice;

        // Set invoice details
        setInvoiceId(invoice.id);
        setInvoiceNumber(sanitizeInput(invoice.invoice_number ?? ""));
        setInvoiceDate(sanitizeInput(invoice.invoice_date ?? ""));
        setDueDate(sanitizeInput(invoice.due_date ?? ""));
        setExchangeRate(sanitizeNumber(invoice.exchange_rate ?? "0"));
        setNotes(sanitizeTextarea(invoice.notes ?? ""));
        setTermsConditions(sanitizeTextarea(invoice.terms ?? ""));

        // Set sales order option (support multiple orders)
        if (Array.isArray(invoice.orders) && invoice.orders.length > 0) {
          setSalesOrderOption(invoice.orders.map(o => ({ value: o.value, label: o.label })));
          invoice.orders.forEach(o => getReturnedProduct(o.value, true));
        } else if (invoice.sale_order_id) {
          setSalesOrderOption([
            { value: invoice.sale_order_id, label: invoice.order_no ?? `Order ${invoice.sale_order_id}` }
          ]);
          getReturnedProduct(invoice.sale_order_id, true);
        } else {
          // No linked order at all — this invoice was created standalone.
          // Edit screen still uses the existing read-only items table (rate/
          // hsn/tax remain editable there) rather than the new create-only
          // Product/Service picker table, so no further item-shape handling
          // is needed here beyond marking the mode for the Sales Order field.
          setInvoiceMode("standalone");
        }

        // Set customer option
        if (invoice.customer) {
          setCustomerOption(invoice.customer);
        }

        // Set currency option — prefer the saved customer currency.
        // Always carry the currency_symbol so the ₹/$ symbol renders on edit.
        if (invoice.customer_currency) {
          // May be a full option object or just an id/name pair.
          const cc = invoice.customer_currency;
          if (typeof cc === "object") {
            setCurrencyOption({
              ...cc,
              value: cc.value ?? cc.currency_id ?? cc.id,
              label: cc.label ?? cc.currency_name ?? cc.name,
              symbol: cc.symbol ?? cc.currency_symbol ?? invoice.currency_symbol,
            });
          } else if (invoice.currency_name) {
            setCurrencyOption({ value: cc, label: invoice.currency_name, symbol: invoice.currency_symbol });
          }
        } else if (invoice.org_currency) {
          setCurrencyOption({
            ...invoice.org_currency,
            symbol: invoice.org_currency.symbol ?? invoice.currency_symbol,
          });
        } else if (invoice.currency_id && invoice.currency_name) {
          setCurrencyOption({
            value: invoice.currency_id,
            label: invoice.currency_name,
            symbol: invoice.currency_symbol,
          });
        }
        if (invoice.organization_currency_id != null) {
          setOrgCurrencyId(invoice.organization_currency_id);
        } else if (invoice.org_currency?.value != null) {
          setOrgCurrencyId(invoice.org_currency.value);
        }
        if (invoice.org_currency_name) {
          setOrgCurrencyName(invoice.org_currency_name);
        }
        if (invoice.org_currency_symbol) {
          setOrgCurrencySymbol(decodeHtmlEntity(invoice.org_currency_symbol) || "");
        }
        setCustomerName(sanitizeInput(invoice.customer?.label ?? ""));
        // Set billing address (from bill_address)
        if (invoice.bill_address) {
          const billAddr = invoice.bill_address;

          const billAddressStr = [
            sanitizeInput(billAddr.address ?? ""),
            sanitizeInput(billAddr.city?.label ?? ""),
            sanitizeInput(billAddr.state?.label ?? ""),
            sanitizeInput(billAddr.country?.label ?? ""),
            sanitizeInput(billAddr.zipcode ?? ""),
          ]
            .filter((part) => part && part !== "null")
            .join(", ");
          setCustomerAddress(billAddressStr);
        }

        // Set shipping address
        if (invoice.ship_address) {
          const shipAddr = invoice.ship_address;
          setShippingAddress(sanitizeInput(shipAddr.address ?? ""));
          setShippingCountryOption(shipAddr.country ?? null);
          setShippingStateOption(shipAddr.state ?? null);
          setShippingCityOption(shipAddr.city ?? null);
          setShippingZipCode(sanitizeInput(shipAddr.zipcode ?? ""));
          if (shipAddr.state) {
            updateStateTaxFlags(shipAddr.state.value);
          }
        }

      
        // Set items
        if (Array.isArray(invoice.items) && invoice.items.length > 0) {
          const isStandaloneInvoice =
            !(Array.isArray(invoice.orders) && invoice.orders.length > 0) &&
            !invoice.sale_order_id;

          const mappedItems = invoice.items.map((lineItem) => ({
            id: lineItem.id,
            order_id: lineItem.sale_order_id ?? invoice.sale_order_id,
            itemName: sanitizeInput(
              lineItem.product?.label ?? lineItem.service_name ?? "",
            ),
            hsnCode: sanitizeInput(lineItem.hsn_no ?? ""),
            prev_quantity: lineItem.quantity ?? "",
            quantity: sanitizeNumber(lineItem.quantity ?? ""),
            return_quantity: "",
            uom: lineItem.uom ?? null,
            rate: sanitizeNumber(lineItem.unit_price ?? ""),
            cgst: sanitizeNumber(lineItem.cgst_percentage ?? ""),
            sgst: sanitizeNumber(lineItem.sgst_percentage ?? ""),
            gst: String(
              Number(lineItem.cgst_percentage ?? 0) +
                Number(lineItem.sgst_percentage ?? 0) ?? "",
            ),
            igst: sanitizeNumber(lineItem.igst_percentage ?? ""),
            taxPercent: sanitizeNumber(lineItem.tax_percentage ?? ""),
            taxAmount: sanitizeNumber(lineItem.tax_amount ?? ""),
            totalAmount: sanitizeNumber(lineItem.total_amount ?? ""),
            from_return: false,
            // Standalone invoices render through the same editable
            // Product/Service item table used on create, so loaded rows need
            // that table's field shape too (item_type/product/unit/service_name)
            // alongside the generic fields above.
            ...(isStandaloneInvoice && {
              rowId: lineItem.id,
              item_type: lineItem.product ? "Product" : "Service",
              product: lineItem.product
                ? { value: lineItem.product.value, label: lineItem.product.label, hsn_no: lineItem.product.hsn_no }
                : null,
              service_name: lineItem.product ? null : sanitizeInput(lineItem.service_name ?? ""),
              unit: lineItem.uom ? { value: lineItem.uom.value, label: lineItem.uom.label } : null,
            }),
          }));
          setItems(mappedItems);

          if (isStandaloneInvoice && mappedItems.length > 0) {
            setStandaloneItemType(mappedItems[0].item_type);
          }
        }

        // Set charges
        if (Array.isArray(invoice.charges) && invoice.charges.length > 0) {
          const mappedCharges = invoice.charges.map((charge) => ({
            label: sanitizeInput(charge.name ?? ""),
            value: sanitizeNumber(charge.amount ?? ""),
            percentage: sanitizeNumber(charge.percentage ?? ""),
          }));
          setCharges(mappedCharges);
        }
        if (Array.isArray(invoice.discounts) && invoice.discounts.length > 0) {
          const mappedDiscounts = invoice.discounts.map((discount) => ({
            label: sanitizeInput(discount.name ?? ""),
            value: sanitizeNumber(discount.amount ?? ""),
            percentage: sanitizeNumber(discount.percentage ?? ""),
          }));
          setDiscounts(mappedDiscounts);
        }
            // Set discount
        if (invoice.discount_amount != null) {
          const isPercentage = invoice.discount_type === "Percentage";
          setDiscountType(isPercentage ? "%" : "Rs.");
          setDiscount(sanitizeNumber(isPercentage ? invoice.discount_percentage ?? "" : invoice.discount_amount ?? ""));
        }

        // Set TDS/TCS
        if (invoice.tds_percentage != null && invoice.tds_percentage > 0) {
          setTdsOption("TDS");
          setTdsPercentage(sanitizeNumber(invoice.tds_percentage ?? "0", 100));
        } else if (
          invoice.tcs_percentage != null &&
          invoice.tcs_percentage > 0
        ) {
          setTdsOption("TCS");
          setTdsPercentage(sanitizeNumber(invoice.tcs_percentage ?? "0", 100));
        }

        // Set adjustment
        setAdjustment(
          Number(sanitizeSignedNumericInput(invoice.adjustment ?? 0)) || 0,
        );

        
      }
    } catch (err) {
      console.error("Error fetching invoice details:", err);
    }
  };

  // Checkbox handlers
  const handleSelectItem = (idx) => {
    const isCurrentlySelected = selectedItems.includes(idx);
    setSelectedItems((prev) =>
      isCurrentlySelected ? prev.filter((i) => i !== idx) : [...prev, idx],
    );
    setItems((prev) =>
      prev.map((item, i) => {
        if (i === idx) {
          return {
            ...item,
            return_quantity: !isCurrentlySelected
              ? String(item.quantity || "")
              : "",
          };
        }
        return item;
      }),
    );
  };

  const handleSelectAllItems = (e) => {
    if (e.target.checked) {
      setSelectedItems(items.map((_, idx) => idx));
      setItems((prev) =>
        prev.map((item) => ({
          ...item,
          return_quantity: String(item.quantity || ""),
        })),
      );
    } else {
      setSelectedItems([]);
      setItems((prev) =>
        prev.map((item) => ({
          ...item,
          return_quantity: "",
        })),
      );
    }
  };

  const handleReturnSelectedItems = () => {
    if (selectedItems.length === 0) return;
    const selectedSet = new Set(selectedItems);
    const movedItems = [];

    const tempItems = [...items];
    const nextItems = [];
    tempItems.forEach((item, idx) => {
      if (!selectedSet.has(idx)) {
        nextItems.push(item);
        return;
      }

      const qty = Number(item.quantity) || 0;
      const parsedReturn = Number(item.return_quantity);
      const returnQty = parsedReturn > 0 ? parsedReturn : qty;
      if (returnQty <= 0) {
        nextItems.push(item);
        return;
      }

      const allowedQty = Math.min(returnQty, qty);

      movedItems.push({
        ...item,
        quantity: String(allowedQty),
        return_quantity: "",
        totalAmount: calculateItemTotal({
          ...item,
          quantity: String(allowedQty),
        }),
      });

      const remainingQty = qty - allowedQty;
      if (remainingQty > 0) {
        nextItems.push({
          ...item,
          quantity: String(remainingQty),
          return_quantity: "",
          totalAmount: calculateItemTotal({
            ...item,
            quantity: String(remainingQty),
          }),
        });
      }
    });
    setItems(nextItems);

    if (movedItems.length > 0) {
      setReturnedItems((prev) => {
        const base = [...prev];

        movedItems.forEach((moved) => {
          const matchIndex = base.findIndex(
            (existing) => existing.id === moved.id,
          );

          if (matchIndex === -1) {
            base.push(moved);
            return;
          }

          const existing = base[matchIndex];
          const existingQty = Number(existing.quantity) || 0;
          const movedQty = Number(moved.quantity) || 0;
          base[matchIndex] = {
            ...existing,
            quantity: String(existingQty + movedQty),
            return_quantity: "",
            totalAmount: calculateItemTotal({
              ...existing,
              quantity: String(existingQty + movedQty),
            }),
          };
        });

        return base;
      });
    }

    setSelectedItems([]);
  };

  const handleSelectReturnedItem = (idx) => {
    const isCurrentlySelected = selectedReturnedItems.includes(idx);
    setSelectedReturnedItems((prev) =>
      isCurrentlySelected ? prev.filter((i) => i !== idx) : [...prev, idx],
    );
    setReturnedItems((prev) =>
      prev.map((item, i) => {
        if (i === idx) {
          return {
            ...item,
            return_quantity: !isCurrentlySelected
              ? String(item.quantity || "")
              : "",
          };
        }
        return item;
      }),
    );
  };

  const handleSelectAllReturnedItems = (e) => {
    if (e.target.checked) {
      setSelectedReturnedItems(returnedItems.map((_, idx) => idx));
      setReturnedItems((prev) =>
        prev.map((item) => ({
          ...item,
          return_quantity: String(item.quantity || ""),
        })),
      );
    } else {
      setSelectedReturnedItems([]);
      setReturnedItems((prev) =>
        prev.map((item) => ({
          ...item,
          return_quantity: "",
        })),
      );
    }
  };

  const handleAddReturnedItemsToInvoice = () => {
    if (selectedReturnedItems.length === 0) return;
    const selectedSet = new Set(selectedReturnedItems);
    const movedItems = [];

    const tempReturnedItems = [...returnedItems];
    const nextReturned = [];

    tempReturnedItems.forEach((item, idx) => {
      if (!selectedSet.has(idx)) {
        nextReturned.push(item);
        return;
      }

      const qty = Number(item.quantity) || 0;
      const parsedReturn = Number(item.return_quantity);
      const returnQty = parsedReturn > 0 ? parsedReturn : qty;
      if (returnQty <= 0) {
        nextReturned.push(item);
        return;
      }

      const allowedQty = Math.min(returnQty, qty);

      movedItems.push({
        ...item,
        quantity: String(allowedQty),
        return_quantity: "",
        totalAmount: calculateItemTotal({
          ...item,
          quantity: String(allowedQty),
        }),
      });

      const remainingQty = qty - allowedQty;
      if (remainingQty > 0) {
        nextReturned.push({
          ...item,
          quantity: String(remainingQty),
          return_quantity: "",
          totalAmount: calculateItemTotal({
            ...item,
            quantity: String(remainingQty),
          }),
        });
      }
    });

    setReturnedItems(nextReturned);

    if (movedItems.length > 0) {
      setItems((prev) => {
        /*  const hasRealItem = prev.some(
          (item) =>
            item.itemName ||
            item.hsnCode ||
            item.quantity ||
            item.uom ||
            item.rate ||
            item.cgst ||
            item.sgst ||
            item.gst ||
            item.igst ||
            item.taxPercent ||
            item.taxAmount ||
            item.totalAmount,
        ); */
        const base = /*  hasRealItem ? */ [...prev]; /* : [] */

        movedItems.forEach((moved) => {
          const matchIndex = base.findIndex(
            (existing) => existing.id === moved.id,
          );

          if (matchIndex === -1) {
            base.push(moved);
            return;
          }

          const existing = base[matchIndex];
          const existingQty = Number(existing.quantity) || 0;
          const movedQty = Number(moved.quantity) || 0;
          base[matchIndex] = {
            ...existing,
            quantity: String(existingQty + movedQty),
            totalAmount: calculateItemTotal({
              ...existing,
              quantity: String(existingQty + movedQty),
            }),
          };
        });

        return base;
      });
    }

    setSelectedReturnedItems([]);
  };

  const toNumber = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Per-item discount (total discount split equally across items). Tax is
  // charged on the amount AFTER this discount is removed — same rule as
  // quotation-create.
  const perItemDiscount =
    toNumber(discountTotal) > 0 && items.length > 0
      ? toNumber(discountTotal) / items.length
      : 0;
  const hasDiscount = toNumber(discountTotal) > 0;

  const money = (n) =>
    `${currencySymbol}${toNumber(n).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Break a line item into parts: base, per-item discount, taxable amount,
  // per-tax amounts, and the discounted+taxed total.
  const getItemBreakdown = (item) => {
    const baseAmount = toNumber(item.rate) * toNumber(item.quantity);
    const taxableAmount = Math.max(baseAmount - perItemDiscount, 0);
    const cgst = toNumber(item.cgst);
    const sgst = toNumber(item.sgst);
    const igst = toNumber(item.igst);
    const cgstAmount = taxableAmount * (cgst / 100);
    const sgstAmount = taxableAmount * (sgst / 100);
    const igstAmount = taxableAmount * (igst / 100);
    const taxAmount = cgstAmount + sgstAmount + igstAmount;
    return {
      baseAmount,
      itemDiscount: perItemDiscount,
      taxableAmount,
      cgst,
      sgst,
      igst,
      cgstAmount,
      sgstAmount,
      igstAmount,
      taxAmount,
      total: taxableAmount + taxAmount,
    };
  };

  const calculateItemTotal = (item) => getItemBreakdown(item).total.toFixed(2);

  // Aggregate GST across items (on the post-discount base).
  const gstTotals = items.reduce(
    (acc, item) => {
      const bd = getItemBreakdown(item);
      acc.cgst += bd.cgstAmount;
      acc.sgst += bd.sgstAmount;
      acc.igst += bd.igstAmount;
      if (bd.cgst > 0 || bd.sgst > 0 || bd.igst > 0) {
        acc.taxableBase += bd.taxableAmount;
      }
      return acc;
    },
    { cgst: 0, sgst: 0, igst: 0, taxableBase: 0 },
  );
  const updateItem = (idx, key, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        const updated = { ...item, [key]: value };
        if (["rate", "quantity", "cgst", "sgst", "igst"].includes(key)) {
          updated.totalAmount = calculateItemTotal(updated);
        }
        return updated;
      }),
    );
  };

  // Returned Items handlers
  const addReturnedItem = () => {
    setReturnedItems((prev) => [
      ...prev,
      {
        itemName: "",
        hsnCode: "",
        quantity: "",
        return_quantity: "",
        rate: "",
        cgst: "",
        sgst: "",
        gst: "",
        igst: "",
        taxPercent: "",
        totalAmount: "",
      },
    ]);
  };

  const removeReturnedItem = (idx) => {
    setReturnedItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateReturnedItem = (idx, key, value) => {
    setReturnedItems((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, [key]: value } : item)),
    );
  };

  // Charges handlers
  const addCharge = () => {
    setCharges((prev) => [
      ...prev,
      {
        label: "",
        value: "",
        percentage: "",
      },
    ]);
  };

  const removeCharge = (idx) => {
    setCharges((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateCharge = (idx, key, value) => {
    setCharges((prev) =>
      prev.map((charge, i) =>
        i === idx ? { ...charge, [key]: value } : charge,
      ),
    );
  };

  // Discount handlers
  const addDiscount = () => {
    setDiscounts((prev) => [
      ...prev,
      {
        label: "",
        value: "",
        percentage: "",
      },
    ]);
  };

  const removeDiscount = (idx) => {
    setDiscounts((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateDiscount = (idx, key, value) => {
    setDiscounts((prev) =>
      prev.map((discount, i) =>
        i === idx ? { ...discount, [key]: value } : discount,
      ),
    );
  };

  // Dropdown handlers
  const handleSalesOrderDropdownChange = async (selectedOptions) => {
    const prevOrders = salesOrderOption || [];
    const nextOrders = selectedOptions || [];

    setSalesOrderOption(nextOrders);

    // Identify added and removed orders
    const prevIds = prevOrders.map((o) => o.value);
    const nextIds = nextOrders.map((o) => o.value);

    const added = nextIds.filter((id) => !prevIds.some(pid => String(pid) === String(id)));
    const removed = prevIds.filter((id) => !nextIds.some(nid => String(nid) === String(id)));

    // Handle added orders
    if (added.length > 0) {
      setRemovedOrders((prev) => prev.filter((id) => !added.includes(id)));
      for (const orderId of added) {
        await getOrderDetails(orderId, true);
        // Returned products only apply to an existing invoice being edited —
        // a brand-new invoice has no returns of its own to show yet.
        if (invoiceId != null) {
          await getReturnedProduct(orderId, true);
        }
      }
    }

    // Handle removed orders
    if (removed.length > 0) {
      setRemovedOrders((prev) => [...new Set([...prev, ...removed])]);
      setItems((prev) => prev.filter((item) => !removed.some(id => String(id) === String(item.order_id))));
      setReturnedItems((prev) =>
        prev.filter((item) => !removed.some(id => String(id) === String(item.order_id))),
      );
    }
  };

  useEffect(() => {
    if (invoiceMode !== "order") return;
    const fetchSalesOrders = async () => {
      if (!customerOption?.value) {
        setSalesOrderOptions([]);
        return;
      }
      setLoadingOrders(true);
      try {
        const response = await apiCall("get", "get_customer_orders", null, {
          params: { customer_id: customerOption.value },
        });
        if (response.data.success) {
          setSalesOrderOptions(response.data.suggestionlist);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        setLoadingOrders(false);
      }
    };
    fetchSalesOrders();
  }, [customerOption, invoiceMode]);

  // Standalone mode only — order mode already gets org currency from the
  // selected Sales Order's response (getOrderDetails, below), same as before
  // this feature existed. Only fetch independently when there is no order to
  // source it from, and only on the create screen (not when editing an
  // existing invoice, which already has its own currency saved).
  useEffect(() => {
    if (invoiceMode !== "standalone" || invoiceId != null) return;
    const fetchOrgCurrency = async () => {
      try {
        const response = await getOrgCurrencyApi();
        if (response?.data?.success) {
          setOrgCurrencyId(response.data.data?.org_currency_id ?? null);
          setOrgCurrencyName(response.data.data?.org_currency_name ?? "");
          setOrgCurrencySymbol(decodeHtmlEntity(response.data.data?.org_currency_symbol) || "");
        }
      } catch (err) {
        // Non-critical — Conversion Rate simply stays hidden if this fails.
      }
    };
    fetchOrgCurrency();
  }, [invoiceMode, invoiceId]);

  const handleCustomerDropdownChange = (option) => {
    setCustomerOption(option);
    setSalesOrderOption([]);
    setItems([]);
    setReturnedItems([]);
    setRemovedOrders([]);
    setCustomerName(option?.label || "");

    if (invoiceMode === "standalone" && option?.value) {
      fetchAndAutoFillStandaloneCustomerDetails(option.value);
    }
    // Order mode: address/currency come from the selected Sales Order instead
    // (getOrderDetails), unchanged from before.
  };

  // Standalone mode only — mirrors order-create.jsx's
  // fetchAndAutoFillCustomerDetails: resolves billing/shipping address from
  // the customer's latest quotation (falling back to their default address)
  // via the same endpoint order-create already uses, and updates the shared
  // CGST/SGST/IGST flags off the resolved shipping state.
  const fetchAndAutoFillStandaloneCustomerDetails = async (customerId) => {
    try {
      const response = await getCustomerDetailsApi(customerId);
      if (response.data && response.data.success === true) {
        const customer = response.data.customer;

        setCustomerAddress(customer.bill_address ?? "");
        setBillingCountryOption(customer.bill_country ?? null);
        setBillingStateOption(customer.bill_state ?? null);
        setBillingCityOption(customer.bill_city ?? null);
        setBillingZipCode(customer.bill_zipcode ?? "");

        setShippingAddress(customer.ship_address ?? "");
        setShippingCountryOption(customer.ship_country ?? null);
        setShippingStateOption(customer.ship_state ?? null);
        setShippingCityOption(customer.ship_city ?? null);
        setShippingZipCode(customer.ship_zipcode ?? "");

        if (customer.ship_state?.value) {
          updateStateTaxFlags(customer.ship_state.value);
        }
      }
    } catch (err) {
      console.error("Error fetching customer details:", err);
    }
  };

  const getOrderDetails = async (orderId, append = false) => {
    try {
      if (!append) setItems([]);

      if (!isValidId(orderId)) return;

      const response = await getOrderDetailsApi(orderId);
      const orderDetails = response?.data?.order ?? response?.data ?? null;
      if (!orderDetails) return;

      setExchangeRate(sanitizeNumber(orderDetails.exchange_rate ?? ""));

      const getOptionLabel = (option) => {
        if (!option) return "";
        return typeof option === "string" ? option : (option.label ?? "");
      };

      const formatAddressValue = (addressObj) => {
        if (!addressObj) return "";
        const parts = [
          addressObj.address,
          getOptionLabel(addressObj.city),
          getOptionLabel(addressObj.state),
          getOptionLabel(addressObj.country),
          addressObj.zipcode,
        ];
        return parts
          .map((part) => sanitizeInput(part ?? ""))
          .filter((part) => part && part !== "null")
          .join(", ");
      };

      setCustomerName(sanitizeInput(orderDetails.customer_name ?? ""));

      if (orderDetails.currency?.value != null) {
        setCurrencyOption(orderDetails.currency);
      } else if (orderDetails.org_currency_id) {
        setCurrencyOption({ value: orderDetails.org_currency_id, label: orderDetails.org_currency_name ?? "INR" });
      }
      if (orderDetails.org_currency_id) {
        setOrgCurrencyId(orderDetails.org_currency_id);
      }
      if (orderDetails.org_currency_name) {
        setOrgCurrencyName(orderDetails.org_currency_name);
      }
      if (orderDetails.org_currency_symbol) {
        setOrgCurrencySymbol(decodeHtmlEntity(orderDetails.org_currency_symbol) || "");
      }

      setCustomerAddress(formatAddressValue(orderDetails.bill_address));

      setShippingAddress(
        sanitizeInput(orderDetails.ship_address?.address ?? ""),
      );
      setShippingCountryOption(orderDetails.ship_address?.country ?? null);
      setShippingStateOption(orderDetails.ship_address?.state ?? null);
      setShippingCityOption(orderDetails.ship_address?.city ?? null);
      setShippingZipCode(
        sanitizeInput(orderDetails.ship_address?.zipcode ?? ""),
      );
      if (orderDetails.ship_address?.state?.value) {
        updateStateTaxFlags(orderDetails.ship_address.state.value);
      }
      setTermsConditions(sanitizeTextarea(orderDetails.terms_condition ?? ""));
      setNotes(sanitizeTextarea(orderDetails.customer_note ?? ""));

     
       /*  const isPercentage = orderDetails.discount_type === "Percentage";
        setDiscountType(isPercentage ? "%" : "Rs.");
        setDiscount(
          isPercentage ?  sanitizeNumber(orderDetails.discount_percentage ?? 0) : sanitizeNumber(orderDetails.discount_amount ?? 0),
        ); */
         if (orderDetails.discount_type === "Percentage") {
          setDiscountType("%");
          const discountValue = Number(orderDetails.discount_percentage ?? 0);
          setDiscount(discountValue);
        } else {
          setDiscountType("Rs.");
          const discountValue = Number(orderDetails.discount_amount ?? 0);
          setDiscount(discountValue);
        }

        const tdsPercentage = Number(orderDetails.tds_percentage ?? 0);
        const tcsPercentage = Number(orderDetails.tcs_percentage ?? 0);
        if (tcsPercentage > 0) {
          setTdsOption("TCS");
          setTdsPercentage(tcsPercentage);
        } else {
          setTdsOption("TDS");
          setTdsPercentage(tdsPercentage);
        }

        setAdjustment(Number(orderDetails.adjustment_amount ?? 0));
        setTotal(Number(orderDetails.total_amount ?? 0));
        /* setDiscounts([
          {
            label: "Order discount",
            value: isPercentage
              ? ""
              : String(orderDetails.discount_value ?? ""),
            percentage: isPercentage
              ? String(orderDetails.discount_value ?? "")
              : "",
          },
        ]); */
      

      const lineItems = Array.isArray(orderDetails.items)
        ? orderDetails.items
        : [];
        const mappedItems = lineItems.map((lineItem) => {
          return {
            id: lineItem.id,
            order_id: orderId,
            itemName: sanitizeInput(
              lineItem.product?.label ?? lineItem.service_name ?? "",
            ),
            hsnCode: sanitizeInput(lineItem.hsn_no ?? "",
            ),
            prev_quantity: lineItem.quantity ?? "",
            quantity: sanitizeNumber(lineItem.quantity ?? ""),
            return_quantity: "",
            uom: lineItem.uom?.label ?? lineItem.uom ?? "",
            rate: sanitizeNumber(lineItem.unit_price ?? ""),
            cgst: sanitizeNumber(lineItem.cgst_percentage ?? ""),
            sgst: sanitizeNumber(lineItem.sgst_percentage ?? ""),
            igst: sanitizeNumber(lineItem.igst_percentage ?? ""),
            taxPercent: sanitizeNumber(lineItem.tax_percentage ?? ""),
            taxAmount: sanitizeNumber(lineItem.tax_amount ?? ""),
            totalAmount: sanitizeNumber(lineItem.total_amount ?? ""),
            from_return: false,
          };
        });
        if (append) {
          setItems((prev) => {
            const existingKeys = new Set(prev.map((i) => `${i.order_id ?? ""}_${i.id}`));
            const nextItems = mappedItems.filter((i) => !existingKeys.has(`${i.order_id ?? ""}_${i.id}`));
            return [...prev, ...nextItems];
          });
        } else {
          setItems(mappedItems);
        }

      setAdjustment(
        Number(
          sanitizeSignedNumericInput(orderDetails.adjustment_amount ?? 0),
        ) || 0,
      );
    } catch (err) {
      console.error("Error fetching order details:", err);
    }
  };

  const getReturnedProduct = async (orderId, append = false) => {
    try {
      if (!append) setReturnedItems([]);
      if (!isValidId(orderId)) return;
      const response = await getReturnedProductApi(orderId);
      const itemsDetails = response?.data?.items ?? [];

      const lineItems = Array.isArray(itemsDetails) ? itemsDetails : [];
      if (lineItems.length > 0) {
        const mappedItems = lineItems.map((lineItem) => {
          return {
            id: lineItem.id,
            order_id: orderId,
            itemName: sanitizeInput(
              lineItem.product?.label ?? lineItem.service_name ?? "",
            ),
            hsnCode: sanitizeInput(lineItem.hsn_no ?? "",
            ),
            quantity: sanitizeNumber(lineItem.quantity ?? ""),
            return_quantity: "",
            uom: lineItem.uom?.label ?? lineItem.uom ?? "",
            rate: sanitizeNumber(lineItem.unit_price ?? ""),
            cgst: sanitizeNumber(lineItem.cgst_percentage ?? ""),
            sgst: sanitizeNumber(lineItem.sgst_percentage ?? ""),
            igst: sanitizeNumber(lineItem.igst_percentage ?? ""),
            taxPercent: sanitizeNumber(lineItem.tax_percentage ?? ""),
            taxAmount: sanitizeNumber(lineItem.tax_amount ?? ""),
            totalAmount: sanitizeNumber(lineItem.total_amount ?? ""),
          };
        });
        if (append) {
          setReturnedItems((prev) => {
            const existingKeys = new Set(prev.map((i) => `${i.order_id ?? ""}_${i.id}`));
            const nextItems = mappedItems.filter((i) => !existingKeys.has(`${i.order_id ?? ""}_${i.id}`));
            return [...prev, ...nextItems];
          });
        } else {
          setReturnedItems(mappedItems);
        }
      }
    } catch (err) {
      console.error("Error fetching order details:", err);
    }
  };

  const [isIgst, setIsIgst] = useState(false);
  const [isCgst, setIsCgst] = useState(false);
  const [isSgst, setIsSgst] = useState(false);

  const updateStateTaxFlags = async (stateId) => {
    await applyStateTaxFlags(stateId, {
      setIsIgst,
      setIsCgst,
      setIsSgst,
    });
  };

  const handleShippingCountryDropdownChange = (option) => {
    setShippingCountryOption(option);
    setShippingStateOption(null);
    setShippingCityOption(null);
  };

  const handleShippingStateDropdownChange = (option) => {
    setShippingStateOption(option);
    setShippingCityOption(null);
  };
  useEffect(() => {
    if (shippingStateOption?.value) {
      updateStateTaxFlags(shippingStateOption.value);
    }
  }, [shippingStateOption]);

  // Fallback for edit/order-derived loads: if the shipping state's tax flags
  // didn't resolve (e.g. no ship_address on the record) but loaded items
  // already carry GST percentages, force the matching columns on so the
  // existing GST data is visible/editable instead of being hidden.
  useEffect(() => {
    if (items.length === 0) return;
    const hasCgst = items.some((item) => toNumber(item.cgst) > 0);
    const hasSgst = items.some((item) => toNumber(item.sgst) > 0);
    const hasIgst = items.some((item) => toNumber(item.igst) > 0);
    if (hasCgst && !isCgst) setIsCgst(true);
    if (hasSgst && !isSgst) setIsSgst(true);
    if (hasIgst && !isIgst) setIsIgst(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const handleShippingCityDropdownChange = (option) => {
    setShippingCityOption(option);
    setShippingStateOption({
      label: option.state_name,
      value: option.state_id,
    });
    setShippingCountryOption({
      label: option.country_name,
      value: option.country_id,
    });
  };

  // Calculate totals
  useEffect(() => {
    const calculatedReturnedTotal = returnedItems.reduce((sum, item) => {
      return sum + (toNumber(item.totalAmount) || 0);
    }, 0);

    setReturnedSubTotal(calculatedReturnedTotal);
    

    const calculatedSubTotalWithoutTax = items.reduce((sum, item) => {
      return sum + (toNumber(item.quantity  * item.rate) || 0);
    }, 0);
    setSubTotalWithOutTax(calculatedSubTotalWithoutTax);

    setSubTotal(calculatedSubTotalWithoutTax);

    // Returned items are shown separately but do NOT reduce the base used for
    // discount, TDS/TCS, adjustment, and the grand total. Those are all
    // calculated from the full subtotal.
    const netAmount = calculatedSubTotalWithoutTax;

    const discountValue = toNumber(discount);
    const discountTotal =
      discountType === "%"
        ? (netAmount * discountValue) / 100
        : discountValue;
    const percentage = Number(tdsPercentage || 0);
    const afterDiscount = netAmount - discountTotal;

    const calculationAdditionalCharges = charges.reduce((sum, item) => {
      return sum + (toNumber(item.value) || 0);
    }, 0);
    const calculationAdditionalDiscount = discounts.reduce((sum, item) => {
      return sum + (toNumber(item.value) || 0);
    }, 0);

    // Item GST charged on the POST-discount base (discount split equally
    // across items). Discount applies on the pre-tax subtotal, so this GST
    // is added back into the grand total.
    const count = items.length || 1;
    const perItemDisc = discountTotal > 0 ? discountTotal / count : 0;
    const totalItemTax = items.reduce((sum, item) => {
      const base = toNumber(item.rate) * toNumber(item.quantity);
      const taxable = Math.max(base - perItemDisc, 0);
      const rate = toNumber(item.cgst) + toNumber(item.sgst) + toNumber(item.igst);
      return sum + taxable * (rate / 100);
    }, 0);
    setTotalTax(totalItemTax);

    // Grand total excludes TDS/TCS — it's shown separately below the Total,
    // calculated FROM the total, but not included back into it.
    const finalTotal =
      afterDiscount +
      totalItemTax +
      toNumber(adjustment) +
      calculationAdditionalCharges -
      calculationAdditionalDiscount;

    // TDS/TCS is calculated from the without-tax (after-discount, pre-GST)
    // amount, but not added back into the Total.
    const tdsAmount = percentage > 0 ? afterDiscount * (percentage / 100) : 0;
    const tdsValue = toNumber(tdsAmount);

    setDiscountTotal(discountTotal);
    setTdsCalcValue(tdsValue);
    setTotal(finalTotal);
  }, [
    items,
    returnedItems,
    discount,
    discountType,
    tdsPercentage,
    adjustment,
    charges,
    discounts,
  ]);

  // Base amount that additional charges/discounts percentages are calculated
  // against — the running total after item discount and TDS/TCS, before any
  // additional charges/discounts and adjustment are applied. Returned items
  // are ignored here (consistent with the main totals calculation).
  const chargeBase =
    toNumber(subTotal) -
    toNumber(discountTotal) +
    (tdsOption === "TCS" ? toNumber(tdsCalcValue) : -toNumber(tdsCalcValue));

  // When the amount changes, derive the equivalent percentage (of chargeBase).
  const handleChargeAmountChange = (idx, rawValue) => {
    const value = sanitizeNumber(rawValue);
    const amount = toNumber(value);
    const percentage =
      chargeBase > 0 ? ((amount / chargeBase) * 100).toFixed(2) : "";
    setCharges((prev) =>
      prev.map((charge, i) =>
        i === idx ? { ...charge, value, percentage } : charge,
      ),
    );
  };

  // When the percentage changes, derive the equivalent amount (of chargeBase).
  const handleChargePercentageChange = (idx, rawValue) => {
    const percentage = sanitizeNumber(rawValue, 100);
    const pct = toNumber(percentage);
    const value =
      chargeBase > 0 ? ((chargeBase * pct) / 100).toFixed(2) : "";
    setCharges((prev) =>
      prev.map((charge, i) =>
        i === idx ? { ...charge, value, percentage } : charge,
      ),
    );
  };

  const handleDiscountAmountChange = (idx, rawValue) => {
    const value = sanitizeNumber(rawValue);
    const amount = toNumber(value);
    const percentage =
      chargeBase > 0 ? ((amount / chargeBase) * 100).toFixed(2) : "";
    setDiscounts((prev) =>
      prev.map((discount, i) =>
        i === idx ? { ...discount, value, percentage } : discount,
      ),
    );
  };

  const handleDiscountPercentageChange = (idx, rawValue) => {
    const percentage = sanitizeNumber(rawValue, 100);
    const pct = toNumber(percentage);
    const value =
      chargeBase > 0 ? ((chargeBase * pct) / 100).toFixed(2) : "";
    setDiscounts((prev) =>
      prev.map((discount, i) =>
        i === idx ? { ...discount, value, percentage } : discount,
      ),
    );
  };

  const submitInvoice = async () => {
    setValidations({});

    // Client-side validations
    const nextValidations = {};

    if (!customerOption?.value) {
      nextValidations.customer_id = "Please select a customer";
    }

    if (invoiceMode === "order" && (!salesOrderOption || salesOrderOption.length === 0)) {
      nextValidations.sale_order_ids = "Please select at least one sales order";
    }

    if (invoiceMode === "standalone" && !customerAddress?.trim()) {
      nextValidations.bill_address = "Billing address is required";
    }

    if (!shippingAddress?.trim()) {
      nextValidations.ship_address = "Shipping address is required";
    }

    if (!shippingCountryOption?.value) {
      nextValidations.ship_country_id = "Shipping country is required";
    }

    if (!shippingStateOption?.value) {
      nextValidations.ship_state_id = "Shipping state is required";
    }

    if (!shippingCityOption?.value) {
      nextValidations.ship_city_id = "Shipping city is required";
    }

    if (!shippingZipCode?.trim()) {
      nextValidations.ship_zipcode = "Shipping zipcode is required";
    }

    if (!invoiceDate) {
      nextValidations.invoice_date = "Invoice date is required";
    }

    const activeItems = invoiceMode === "standalone"
      ? items.filter((item) => (item.item_type || standaloneItemType) === standaloneItemType)
      : items;

    if (!activeItems || activeItems.length === 0) {
      nextValidations.items = "Please add at least one item";
    } else {
      const hasInvalidItem = activeItems.some((item) => {
        if (invoiceMode === "standalone") {
          const currentType = item.item_type || standaloneItemType;
          if (currentType === "Product" && !item.product?.value) return true;
          if (currentType === "Service" && !item.service_name?.trim()) return true;
        }
        return !item.quantity || Number(item.quantity) <= 0 || item.rate === "" || Number(item.rate) < 0;
      });

      if (hasInvalidItem) {
        nextValidations.items = "Please complete all item fields with valid product/service, quantity (> 0), and price (>= 0)";
      }
    }

    if (Object.keys(nextValidations).length > 0) {
      setValidations(nextValidations);
      const firstMessage = Object.values(nextValidations)[0];
      setSnackbarMessage(firstMessage);
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }

    setIsSaving(true);

    const sharedPayload = {
      exchange_rate: sanitizeNumber(exchangeRate) || 1,
      currency_id: currencyOption?.value || null,
      invoice_date: sanitizeInput(invoiceDate),
      due_date: sanitizeInput(dueDate),
      notes: sanitizeTextarea(notes),
      terms: sanitizeTextarea(termsConditions),
      ship_address: sanitizeInput(shippingAddress),
      ship_city_id: shippingCityOption?.value || null,
      ship_state_id: shippingStateOption?.value || null,
      ship_country_id: shippingCountryOption?.value || null,
      ship_zipcode: sanitizeInput(shippingZipCode),
      discount_type: discountType === "%" ? "Percentage" : "Amount",
      discount_value: sanitizeNumber(discount),
      tds_type: tdsOption,
      tds_percentage:
        tdsOption === "TDS" ? sanitizeNumber(tdsPercentage, 100) : 0,
      tcs_percentage:
        tdsOption === "TCS" ? sanitizeNumber(tdsPercentage, 100) : 0,
      adjustment_amount: sanitizeSignedNumericInput(adjustment),
      charges: charges.filter((item) => item.label && item.value).map((charge) => ({
        name: sanitizeInput(charge.label),
        percentage: sanitizeNumber(charge.percentage, 100),
        amount: sanitizeNumber(charge.value),
      })),
      discounts: discounts.filter((item) => item.label && item.value).map((discount) => ({
        name: sanitizeInput(discount.label),
        percentage: sanitizeNumber(discount.percentage, 100),
        amount: sanitizeNumber(discount.value),
      })),
    };

    const payload =
      invoiceMode === "standalone"
        ? {
            ...sharedPayload,
            invoice_type: "standalone",
            customer_id: customerOption?.value || null,
            item_type: standaloneItemType,
            bill_address: sanitizeInput(customerAddress),
            bill_city_id: billingCityOption?.value || null,
            bill_state_id: billingStateOption?.value || null,
            bill_country_id: billingCountryOption?.value || null,
            bill_zipcode: sanitizeInput(billingZipCode),
            items: activeItems.map((item) => ({
              id: item.id ? sanitizeInput(String(item.id)) : null,
              item_type: item.item_type || standaloneItemType,
              product_id: (item.item_type || standaloneItemType) === "Product" ? item.product?.value || null : null,
              service_name: (item.item_type || standaloneItemType) === "Service" ? sanitizeInput(item.service_name ?? "") : null,
              uom_id: item.unit?.value || null,
              hsn_no: sanitizeInput(item.hsnCode ?? ""),
              quantity: sanitizeNumber(item.quantity),
              unit_price: sanitizeNumber(item.rate),
              cgst_percentage: sanitizeNumber(item.cgst, 100),
              sgst_percentage: sanitizeNumber(item.sgst, 100),
              igst_percentage: sanitizeNumber(item.igst, 100),
              from_return: false,
            })),
            returned_items: [],
          }
        : {
            ...sharedPayload,
            invoice_type: "order",
            sale_order_id: salesOrderOption?.[0]?.value || null,
            sale_order_ids: (salesOrderOption || []).map((o) => o.value),
            removed_orders: removedOrders,
            items: items.map((item) => ({
              id: item.id ?? null,
              hsn_no: sanitizeInput(item.hsnCode ?? ""),
              quantity: sanitizeNumber(item.quantity),
              unit_price: sanitizeNumber(item.rate),
              cgst_percentage: sanitizeNumber(item.cgst, 100),
              sgst_percentage: sanitizeNumber(item.sgst, 100),
              igst_percentage: sanitizeNumber(item.igst, 100),
              from_return: Boolean(item.from_return),
            })),
            returned_items: returnedItems.map((item) => ({
              id: item.id ?? null,
              quantity: sanitizeNumber(item.quantity),
            })),
          };

    try {
      let response;
      if (invoiceId != null) {
        response = await updateInvoiceApi(payload, invoiceId);
      } else {
        response = await createInvoiceApi(payload);
      }

      if (response?.data?.success === true || (response?.status === 200 && response?.data?.success !== false)) {
        setSnackbarMessage(sanitizeInput(response?.data?.message || "Invoice saved successfully"));
        setSnackbarStatus(true);
        handleSnackbarShow();
        setTimeout(() => {
          navigate("/invoice/list");
        }, 800);
      } else {
        const errorData = response?.data?.error;
        const validationTemp = {};
        let errorMsg = response?.data?.message || "Failed to save invoice";

        if (errorData) {
          if (typeof errorData === "object" && !Array.isArray(errorData)) {
            Object.entries(errorData).forEach(([key, value]) => {
              const safeValue = Array.isArray(value) && value.length > 0 ? value[0] : (typeof value === "string" ? value : "");
              validationTemp[key] = typeof safeValue === "string" ? sanitizeInput(safeValue) : "";
            });
            const firstErr = Object.values(validationTemp).find(Boolean);
            if (firstErr && (!errorMsg || errorMsg === "Validation Error")) {
              errorMsg = firstErr;
            }
          } else if (Array.isArray(errorData)) {
            errorMsg = errorData.filter(Boolean).join(". ");
          } else if (typeof errorData === "string") {
            errorMsg = errorData;
          }
        }

        setValidations(validationTemp);
        setSnackbarMessage(sanitizeInput(errorMsg));
        setSnackbarStatus(false);
        handleSnackbarShow();
      }
    } catch (err) {
      console.error("Failed to create invoice", err);
      const errMsg = err?.response?.data?.message || err?.message || "Failed to create invoice";
      setSnackbarMessage(sanitizeInput(errMsg));
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="page-container ">
      <div className="order-header mt-2 px-2 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-3">
          <img
            src={Back}
            alt="back"
            className="back-icon"
            onClick={() => navigate(-1)}
          />
          <div className="page-title">
            {" "}
            {invoiceId ? "Edit Invoice" : "New Invoice"}
          </div>
        </div>

        <div>
          <Button
            variant="light"
            className="btn-cancel me-2"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            className="btn-save"
            onClick={submitInvoice}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>

      <div className="order-page" style={{ display: "flex", flexWrap: "wrap" }}>
        {/* Left Column */}
        <div className="col-md-7 col-sm-12 mt-3">
          {/* Invoice Details Section */}
          <div className="order-card">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title">Invoice details</h5>
              <div
              >
               <div className="item-type-toggle">
                  <label
                    className={`item-type-option ${invoiceMode === "order" ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="invoiceMode"
                      value="order"
                      checked={invoiceMode === "order"}
                      disabled={!!invoiceId}
                      onChange={() => setInvoiceMode("order")}
                    />
                    <span className="item-type-text">From Sales Order</span>
                  </label>
                  <label
                    className={`item-type-option ${invoiceMode === "standalone" ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="invoiceMode"
                      value="standalone"
                      checked={invoiceMode === "standalone"}
                      disabled={!!invoiceId}
                      onChange={() => {
                        setInvoiceMode("standalone");
                        setSalesOrderOption([]);
                        setItems([]);
                        setReturnedItems([]);
                        setRemovedOrders([]);
                      }}
                    />
                    <span className="item-type-text">Standalone</span>
                  </label>
                </div>
              </div>
            </div>

            {/* <Row className="mb-3">
              <Col md={6}>
                <label className="field-label">Invoice Type</label>
                <div className="item-type-toggle">
                  <label
                    className={`item-type-option ${invoiceMode === "order" ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="invoiceMode"
                      value="order"
                      checked={invoiceMode === "order"}
                      disabled={!!invoiceId}
                      onChange={() => setInvoiceMode("order")}
                    />
                    <span className="item-type-text">From Sales Order</span>
                  </label>
                  <label
                    className={`item-type-option ${invoiceMode === "standalone" ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="invoiceMode"
                      value="standalone"
                      checked={invoiceMode === "standalone"}
                      disabled={!!invoiceId}
                      onChange={() => setInvoiceMode("standalone")}
                    />
                    <span className="item-type-text">Standalone</span>
                  </label>
                </div>
                {invoiceId != null && (
                  <div className="fs-12" style={{ color: "#8790A1", marginTop: "4px" }}>
                    Invoice type can't be changed after creation.
                  </div>
                )}
              </Col>
            </Row> */}

            <Row>
              <Col md={6}>
                <label className="field-label">
                  Customer <span className="required">*</span>
                </label>
                <SearchWithAPI
                  onDropdownChange={handleCustomerDropdownChange}
                  apiUrl={apiUrl + "get_customers"}
                  params={{}}
                  placeholder={"Select"}
                  selectedOption={customerOption}
                />
                <ErrorMessage message={validations.customer_id} />
              </Col>

              {invoiceMode === "order" && (
                <Col md={6}>
                  <label className="field-label">
                    Sales orders <span className="required">*</span>
                  </label>
                  <Select
                    isMulti
                    onMenuOpen={() => {
                      // Logic to fetch if needed or rely on onInputChange
                    }}
                    onChange={handleSalesOrderDropdownChange}
                    options={salesOrderOptions}
                    isLoading={loadingOrders}
                    value={salesOrderOption}
                    styles={multiselectCustomStyles}
                    placeholder={"Select Sales orders"}
                    isDisabled={!customerOption}
                    noOptionsMessage={() =>
                      customerOption
                        ? "No orders found"
                        : "Please select a customer first"
                    }
                  />
                  <ErrorMessage message={validations.sale_order_ids} />
                </Col>
              )}

              {invoiceNumber && (
                <Col md={6} className="mt-3">
                  <label className="field-label">Invoice Number</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Auto Fill"
                    value={invoiceNumber}
                    readOnly
                    style={{
                      backgroundColor: "#F8F8F8",
                      border: "0.8px solid #EBEBEB",
                    }}
                  />
                  <ErrorMessage message={validations.invoice_number} />
                </Col>
              )}


              <Col md={6} className="mt-3">
                <label className="field-label">Currency <span className="required">*</span></label>
                <SearchWithAPI
                  onDropdownChange={handleCurrencyDropdownChange}
                  apiUrl={apiUrl + "get_currency"}
                  params={{}}
                  placeholder={"Select"}
                  selectedOption={currencyOption}
                />
                <ErrorMessage message={validations.currency_id} />
              </Col>

              {showConversionRate && (
                <Col md={6} className="mt-3">
                  <label className="field-label">Conversion Rate</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter conversion rate"
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(sanitizeNumber(e.target.value))}
                  />
                  <ErrorMessage message={validations.exchange_rate} />
                </Col>
              )}

              <Col md={6} className="mt-3">
                <label className="field-label">Invoice Date</label>
                <input
                  type="date"
                  className="form-control"
                  placeholder="dd/mm/yyyy at hh:mm"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
                <ErrorMessage message={validations.invoice_date} />
              </Col>

              <Col md={6} className="mt-3">
                <label className="field-label">Due Date</label>
                <input
                  type="date"
                  className="form-control"
                  placeholder="dd/mm/yyyy at hh:mm"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                <ErrorMessage message={validations.due_date} />
              </Col>

              {/*  <Col md={6} className="mt-3">
                <label className="field-label">Invoice Status</label>
                <select
                  className="form-control"
                  value={invoiceStatus}
                  onChange={(e) => setInvoiceStatus(e.target.value)}
                >
                  <option value="Sent">Sent</option>
                  <option value="Draft">Draft</option>
                  <option value="Paid">Paid</option>
                  <option value="Overdue">Overdue</option>
                </select>
              </Col> */}
            </Row>
          </div>
        </div>
        {/* Right Column */}
        <div className="col-md-5 col-sm-12 mt-3">
          {/* Customer Details */}
          <div className="order-card">
            <h5 className="card-title">Customer details</h5>

            <Row>
              <Col md={12}>
                <label className="field-label">Customer Name</label>

                <input
                  className="form-control"
                  placeholder="Auto Fill"
                  value={customerName}
                  readOnly
                  style={{
                    backgroundColor: "#F8F8F8",
                    border: "0.8px solid #EBEBEB",
                  }}
                />
              </Col>

              <Col md={12} className="mt-3">
                <label className="field-label">Customer Address</label>
                <textarea
                  className="form-control"
                  style={{
                    Height: "100px",
                    backgroundColor: isStandaloneCreate ? "#fff" : "#F8F8F8",
                    border: validations.bill_address ? "0.8px solid #DC3545" : "0.8px solid #EBEBEB",
                  }}
                  placeholder="Auto Fill"
                  value={customerAddress}
                  rows={4}
                  readOnly={!isStandaloneCreate}
                  onChange={
                    isStandaloneCreate
                      ? (e) => {
                          setCustomerAddress(sanitizeTextarea(e.target.value));
                          if (validations.bill_address) {
                            setValidations((prev) => ({ ...prev, bill_address: "" }));
                          }
                        }
                      : undefined
                  }
                />
                <ErrorMessage message={validations.bill_address} />
              </Col>

              {isStandaloneCreate && (
                <>
                  <Col md={6} className="mt-3">
                    <label className="field-label">Billing Country</label>
                    <SearchWithAPI
                      onDropdownChange={handleBillingCountryDropdownChange}
                      apiUrl={apiAccUrl + "get_country"}
                      params={{}}
                      placeholder={"Select"}
                      selectedOption={billingCountryOption}
                    />
                  </Col>

                  <Col md={6} className="mt-3">
                    <label className="field-label">Billing State</label>
                    <SearchWithAPI
                      onDropdownChange={handleBillingStateDropdownChange}
                      apiUrl={apiAccUrl + "get_state"}
                      params={{ country_id: billingCountryOption?.value || "" }}
                      placeholder={"Select"}
                      selectedOption={billingStateOption}
                    />
                  </Col>

                  <Col md={6} className="mt-3">
                    <label className="field-label">Billing City</label>
                    <SearchWithAPI
                      onDropdownChange={handleBillingCityDropdownChange}
                      apiUrl={apiAccUrl + "get_city"}
                      params={{ state_id: billingStateOption?.value || "" }}
                      placeholder={"Select"}
                      selectedOption={billingCityOption}
                    />
                  </Col>

                  <Col md={6} className="mt-3">
                    <label className="field-label">Billing Zip code</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter here..."
                      value={billingZipCode}
                      onChange={(e) => setBillingZipCode(sanitizeTextarea(e.target.value))}
                    />
                  </Col>
                </>
              )}
            </Row>
          </div>
        </div>

        {/* Shipping Address Section */}
        <div className="col-md-6 col-sm-12 mt-3">
          <div className=" order-card">
            <h5 className="card-title">Shipping Address</h5>

            <Row>
              <Col md={12}>
                <label className="field-label">Address</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter here..."
                  value={shippingAddress}
                  onChange={(e) =>
                    setShippingAddress(sanitizeTextarea(e.target.value))
                  }
                />
                <ErrorMessage message={validations.ship_address} />
              </Col>

              <Col md={6} className="mt-3">
                <label className="field-label">Country</label>
                <SearchWithAPI
                  onDropdownChange={handleShippingCountryDropdownChange}
                  apiUrl={apiAccUrl + "get_country"}
                  params={{}}
                  placeholder={"Select"}
                  selectedOption={shippingCountryOption}
                />
                <ErrorMessage message={validations.ship_country_id} />
              </Col>

              <Col md={6} className="mt-3">
                <label className="field-label">State</label>
                <SearchWithAPI
                  onDropdownChange={handleShippingStateDropdownChange}
                  apiUrl={apiAccUrl + "get_state"}
                  params={{
                    country_id: shippingCountryOption?.value || "",
                  }}
                  placeholder={"Select"}
                  selectedOption={shippingStateOption}
                />
                <ErrorMessage message={validations.ship_state_id} />
              </Col>

              <Col md={6} className="mt-3">
                <label className="field-label">City</label>
                <SearchWithAPI
                  onDropdownChange={handleShippingCityDropdownChange}
                  apiUrl={apiAccUrl + "get_city"}
                  params={{
                    state_id:
                      shippingStateOption != null
                        ? shippingStateOption.value
                        : "",
                  }}
                  placeholder={"Select"}
                  selectedOption={shippingCityOption}
                />
                <ErrorMessage message={validations.ship_city_id} />
              </Col>

              <Col md={6} className="mt-3">
                <label className="field-label">Zip code</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter here..."
                  value={shippingZipCode}
                  onChange={(e) =>
                    setShippingZipCode(sanitizeTextarea(e.target.value))
                  }
                />
                <ErrorMessage message={validations.ship_zipcode} />
              </Col>
            </Row>
          </div>
        </div>

        {/* Terms & Conditions Section */}
        <div className="col-md-6 col-sm-12 mt-3">
          <div className=" order-card">
            <div className="card-title">Terms & Conditions</div>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Enter here..."
              value={termsConditions}
              onChange={(e) =>
                setTermsConditions(sanitizeTextarea(e.target.value))
              }
            />
            <div className="card-title mt-2">Notes</div>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Enter here..."
              value={notes}
              onChange={(e) => setNotes(sanitizeTextarea(e.target.value))}
            />
          </div>
        </div>
        {/* Products Section */}
        <div className="col-sm-12  mt-3">
          <div className=" order-card">
            {!isStandaloneItems && (
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title">Products : {items.length}</h5>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleReturnSelectedItems()}
                  disabled={selectedItems.length === 0}
                >
                  Return
                </Button>
              </div>
            )}
            {isStandaloneItems && (
              <div className="d-flex align-items-center mb-3">
                <h5 className="card-title mb-0 me-3">Items :</h5>
                <div className="item-type-toggle">
                  <label
                    className={`item-type-option ${standaloneItemType === "Product" ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="standaloneItemType"
                      value="Product"
                      checked={standaloneItemType === "Product"}
                      onChange={(e) => setStandaloneItemType(e.target.value)}
                    />
                    <span className="item-type-text">Product</span>
                  </label>
                  <label
                    className={`item-type-option ${standaloneItemType === "Service" ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="standaloneItemType"
                      value="Service"
                      checked={standaloneItemType === "Service"}
                      onChange={(e) => setStandaloneItemType(e.target.value)}
                    />
                    <span className="item-type-text">Service</span>
                  </label>
                </div>
              </div>
            )}

            <div className="items-table-wrapper">
            {!isStandaloneItems && (
              <table className="items-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}>
                      <input
                        type="checkbox"
                        onChange={handleSelectAllItems}
                        checked={
                          selectedItems.length === items.length &&
                          items.length > 0
                        }
                      />
                    </th>
                    <th>ITEM NAME</th>
                    <th>HSN CODE</th>
                    <th>QUANTITY</th>
                    {selectedItems.length > 0 && <th>RETURN QTY</th>}
                    <th>UOM</th>
                    <th>RATE</th>
                    <th>AMOUNT</th>
                    {hasDiscount && <th>DISCOUNT</th>}
                    {isCgst && <th>CGST(%)</th>}
                    {isSgst && <th>SGST(%)</th>}
                    {isIgst && <th>IGST(%)</th>}
                    <th>TOTAL AMOUNT</th>
                    {/* <th></th> */}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(idx)}
                          onChange={() => handleSelectItem(idx)}
                        />
                      </td>
                      <td>
                        <span className="table-label">
                          {item.itemName || "---"}
                        </span>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="table-input"
                          placeholder="HSN Code"
                          value={item.hsnCode ?? ""}
                          onChange={(e) =>
                            updateItem(
                              idx,
                              "hsnCode",
                              sanitizeInput(e.target.value),
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="table-input"
                          placeholder="Enter here..."
                          value={item.quantity}
                          readOnly
                          /* onChange={(e) =>
                            updateItem(idx, "quantity", e.target.value)
                          } */
                        />
                      </td>
                      {selectedItems.length > 0 && (
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            placeholder="Enter here..."
                            value={item.return_quantity}
                            readOnly={!selectedItems.includes(idx)}
                            onChange={(e) => {
                              const maxQty = Number(item.quantity) || 0;
                              const nextValue = sanitizeNumber(
                                e.target.value,
                                maxQty,
                              );
                              const nextNumber = Number(nextValue);
                              if (Number.isNaN(nextNumber)) return;
                              if (nextNumber <= maxQty) {
                                updateItem(idx, "return_quantity", nextValue);
                              }
                            }}
                          />
                        </td>
                      )}
                      <td>
                        <span className="table-label">
                          {item.uom?.label || item.uom || "---"}
                        </span>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="table-input"
                          placeholder={`${currencySymbol} Amount`}
                          value={item.rate}
                          onChange={(e) =>
                            updateItem(
                              idx,
                              "rate",
                              sanitizeNumber(e.target.value),
                            )
                          }
                        />
                      </td>
                      {/* Amount = Rate × Quantity (read-only) */}
                      <td>
                        <input
                          type="text"
                          className="table-input"
                          readOnly
                          value={getItemBreakdown(item).baseAmount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        />
                      </td>
                      {hasDiscount && (
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            readOnly
                            value={getItemBreakdown(item).itemDiscount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          />
                        </td>
                      )}
                      {isCgst && (
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            placeholder="%"
                            value={item.cgst}
                            onChange={(e) =>
                              updateItem(
                                idx,
                                "cgst",
                                sanitizeNumber(e.target.value, 100),
                              )
                            }
                          />
                        </td>
                      )}
                      {isSgst && (
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            placeholder="%"
                            value={item.sgst}
                            onChange={(e) =>
                              updateItem(
                                idx,
                                "sgst",
                                sanitizeNumber(e.target.value, 100),
                              )
                            }
                          />
                        </td>
                      )}
                      {isIgst && (
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            placeholder="%"
                            value={item.igst}
                            onChange={(e) =>
                              updateItem(
                                idx,
                                "igst",
                                sanitizeNumber(e.target.value, 100),
                              )
                            }
                          />
                        </td>
                      )}
                      <td>
                        {(() => {
                          const bd = getItemBreakdown(item);
                          const rows = [
                            ["Amount", money(bd.baseAmount)],
                            ...(bd.itemDiscount > 0
                              ? [["Discount", `- ${money(bd.itemDiscount)}`]]
                              : []),
                            ["Taxable", money(bd.taxableAmount)],
                            ...(bd.cgst > 0
                              ? [[`CGST (${bd.cgst}%)`, `+ ${money(bd.cgstAmount)}`]]
                              : []),
                            ...(bd.sgst > 0
                              ? [[`SGST (${bd.sgst}%)`, `+ ${money(bd.sgstAmount)}`]]
                              : []),
                            ...(bd.igst > 0
                              ? [[`IGST (${bd.igst}%)`, `+ ${money(bd.igstAmount)}`]]
                              : []),
                            ["Total", money(bd.total), true],
                          ];
                          return (
                            <div className="table-input d-flex align-items-center justify-content-between gap-1">
                              <span>
                                {bd.total.toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                              <InfoTooltip rows={rows} />
                            </div>
                          );
                        })()}
                      </td>
                      {/*  <td>
                        {items.length > 1 && (
                          <button
                            className="delete-row-btn"
                            onClick={() => removeItem(idx)}
                            type="button"
                          >
                            <img
                              src={Delete}
                              alt="delete"
                              className="delete-icon"
                            />
                          </button>
                        )}
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {isStandaloneItems && (
              <>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>{standaloneItemType === "Service" ? "SERVICE NAME" : "ITEM NAME"}</th>
                      <th>HSN CODE</th>
                      <th>QUANTITY</th>
                      <th>UOM</th>
                      <th>RATE</th>
                      <th>AMOUNT</th>
                      {hasDiscount && <th>DISCOUNT</th>}
                      {isCgst && <th>CGST(%)</th>}
                      {isSgst && <th>SGST(%)</th>}
                      {isIgst && <th>IGST(%)</th>}
                      <th>TOTAL AMOUNT</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items
                      .filter((item) => item.item_type === standaloneItemType)
                      .map((item, idx) => {
                        const realIdx = items.indexOf(item);
                        return (
                          <tr key={item.rowId ?? realIdx}>
                            {standaloneItemType === "Product" && (
                              <td>
                                <CommonDropdown
                                  apiUrl={`${apiUrl}get_products?term=`}
                                  placeholder="Select"
                                  value={item.product}
                                  onSelect={(option) => {
                                    setItems((prev) =>
                                      prev.map((it, i) =>
                                        i === realIdx
                                          ? {
                                              ...it,
                                              product: option,
                                              hsnCode: sanitizeInput(option?.hsn_no ?? option?.hsn ?? ""),
                                            }
                                          : it,
                                      ),
                                    );
                                  }}
                                />
                              </td>
                            )}
                            {standaloneItemType === "Service" && (
                              <td>
                                <SearchWithFreeTextAPI
                                  onDropdownChange={(option) => {
                                    setItems((prev) =>
                                      prev.map((it, i) =>
                                        i === realIdx
                                          ? {
                                              ...it,
                                              service_name: sanitizeInput(option.value),
                                              hsnCode: sanitizeInput(option?.hsn_no ?? option?.hsn ?? ""),
                                            }
                                          : it,
                                      ),
                                    );
                                  }}
                                  onChangeSearch={(term) => {
                                    setItems((prev) =>
                                      prev.map((it, i) =>
                                        i === realIdx ? { ...it, service_name: sanitizeInput(term) } : it,
                                      ),
                                    );
                                  }}
                                  apiUrl={apiUrl + "get_services"}
                                  params={{}}
                                  searchLimit={0}
                                  placeholder={"Search service..."}
                                  selectedValue={item.service_name}
                                />
                              </td>
                            )}
                            <td>
                              <input
                                type="text"
                                className="table-input"
                                placeholder="HSN Code"
                                value={item.hsnCode ?? ""}
                                onChange={(e) => updateItem(realIdx, "hsnCode", sanitizeInput(e.target.value))}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="table-input"
                                placeholder="Qty"
                                value={item.quantity ?? ""}
                                onChange={(e) =>
                                  updateItem(
                                    realIdx,
                                    "quantity",
                                    e.target.value === "" ? "" : sanitizeNumericInput(e.target.value),
                                  )
                                }
                              />
                            </td>
                            <td>
                              <CommonDropdown
                                apiUrl={`${apiAccUrl}get_uom?term=`}
                                placeholder="Select"
                                value={item.unit}
                                onSelect={(option) => {
                                  setItems((prev) => prev.map((it, i) => (i === realIdx ? { ...it, unit: option } : it)));
                                }}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="table-input"
                                placeholder={`${currencySymbol} Amount`}
                                value={item.rate ?? ""}
                                onChange={(e) =>
                                  updateItem(
                                    realIdx,
                                    "rate",
                                    e.target.value === "" ? "" : sanitizeNumericInput(e.target.value),
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="table-input"
                                readOnly
                                value={getItemBreakdown(item).baseAmount.toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              />
                            </td>
                            {hasDiscount && (
                              <td>
                                <input
                                  type="text"
                                  className="table-input"
                                  readOnly
                                  value={getItemBreakdown(item).itemDiscount.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                />
                              </td>
                            )}
                            {isCgst && (
                              <td>
                                <input
                                  type="text"
                                  className="table-input"
                                  placeholder="Value %"
                                  value={item.cgst ?? ""}
                                  onChange={(e) =>
                                    updateItem(
                                      realIdx,
                                      "cgst",
                                      e.target.value === "" ? "" : sanitizeNumericInput(e.target.value),
                                    )
                                  }
                                />
                              </td>
                            )}
                            {isSgst && (
                              <td>
                                <input
                                  type="text"
                                  className="table-input"
                                  placeholder="Value %"
                                  value={item.sgst ?? ""}
                                  onChange={(e) =>
                                    updateItem(
                                      realIdx,
                                      "sgst",
                                      e.target.value === "" ? "" : sanitizeNumericInput(e.target.value),
                                    )
                                  }
                                />
                              </td>
                            )}
                            {isIgst && (
                              <td>
                                <input
                                  type="text"
                                  className="table-input"
                                  placeholder="Value %"
                                  value={item.igst ?? ""}
                                  onChange={(e) =>
                                    updateItem(
                                      realIdx,
                                      "igst",
                                      e.target.value === "" ? "" : sanitizeNumericInput(e.target.value),
                                    )
                                  }
                                />
                              </td>
                            )}
                            <td>
                              {(() => {
                                const bd = getItemBreakdown(item);
                                return (
                                  <span>
                                    {bd.total.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              {items.length > 1 && (
                                <button
                                  className="delete-row-btn"
                                  type="button"
                                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== realIdx))}
                                >
                                  <img src={Delete} alt="delete" className="delete-icon" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <div className="add-item-button mb-3 mt-2">
                  <Button
                    variant="link"
                    onClick={() =>
                      setItems((prev) => [
                        ...prev,
                        {
                          rowId: `standalone-${Date.now()}-${prev.length}`,
                          item_type: standaloneItemType,
                          hsnCode: "",
                          quantity: "",
                          unit: null,
                          rate: "",
                          cgst: "",
                          sgst: "",
                          igst: "",
                          totalAmount: "",
                        },
                      ])
                    }
                  >
                    + Add Item
                  </Button>
                </div>
              </>
            )}
              <ErrorMessage message={validations.items} />
              <div
                className="col-12"
                style={{ display: "flex", justifyContent: "end" }}
              >
                <div className="col-sm-7 mt-4">
                  <div>
                    <h5 className="card-title">Add charges</h5>
                    {charges.map((charge, idx) => (
                      <div key={idx} className="mb-2 d-flex gap-2">
                        <div className="col-md-6">
                          <label className="field-label">Charges</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter charge label"
                            value={charge.label}
                            onChange={(e) =>
                              updateCharge(
                                idx,
                                "label",
                                sanitizeInput(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="col-xs-12 col-md-4">
                          <label className="field-label">Values</label>
                          <div
                            className="d-flex"
                            style={{
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                              overflow: "hidden",
                            }}
                          >
                            <input
                              type="text"
                              className="form-control"
                              placeholder={`${currencySymbol} Amount`}
                              value={charge.value}
                              onChange={(e) =>
                                handleChargeAmountChange(idx, e.target.value)
                              }
                              style={{
                                flex: 1,
                                border: "none",
                                borderRight: "1px solid #dee2e6",
                                borderRadius: "0px",
                                outline: "none",
                                boxShadow: "none",
                              }}
                            />
                            <input
                              type="text"
                              className="form-control"
                              placeholder="% "
                              value={charge.percentage}
                              onChange={(e) =>
                                handleChargePercentageChange(idx, e.target.value)
                              }
                              style={{
                                flex: 1,
                                border: "none",
                                borderRadius: "0px",
                                outline: "none",
                                boxShadow: "none",
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                          {charges.length > 1 && (
                            <button
                              className="delete-row-btn"
                              onClick={() => removeCharge(idx)}
                              type="button"
                            >
                              <img
                                src={Delete}
                                alt="delete"
                                className="delete-icon"
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <Button className="add-charge-btn mt-2" onClick={addCharge}>
                      + Add charge
                    </Button>

                    <div className="mt-2"></div>

                    {discounts.map((discount, idx) => (
                      <div key={idx} className="mb-2 d-flex gap-2">
                        <div className="col-md-6">
                          <label className="field-label">Discount</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter discount label"
                            value={discount.label}
                            onChange={(e) =>
                              updateDiscount(
                                idx,
                                "label",
                                sanitizeInput(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="col-xs-12 col-md-4">
                          <label className="field-label">Values</label>
                          <div
                            className="d-flex"
                            style={{
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                              overflow: "hidden",
                            }}
                          >
                            <input
                              type="text"
                              className="form-control"
                              placeholder={`${currencySymbol} Amount`}
                              value={discount.value}
                              onChange={(e) =>
                                handleDiscountAmountChange(idx, e.target.value)
                              }
                              style={{
                                flex: 1,
                                border: "none",
                                borderRight: "1px solid #dee2e6",
                                borderRadius: "0px",
                                outline: "none",
                                boxShadow: "none",
                              }}
                            />
                            <input
                              type="text"
                              className="form-control"
                              placeholder="% "
                              value={discount.percentage}
                              onChange={(e) =>
                                handleDiscountPercentageChange(idx, e.target.value)
                              }
                              style={{
                                flex: 1,
                                border: "none",
                                borderRadius: "0px",
                                outline: "none",
                                boxShadow: "none",
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                          {discounts.length > 1 && (
                            <button
                              className="delete-row-btn"
                              onClick={() => removeDiscount(idx)}
                              type="button"
                            >
                              <img
                                src={Delete}
                                alt="delete"
                                className="delete-icon"
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <Button
                      className="add-charge-btn mt-2"
                      onClick={addDiscount}
                    >
                      + Add discount
                    </Button>
                  </div>
                </div>
                <div
                  className="subtotal-card col-12 col-sm-5"
                  style={{
                    backgroundColor: "#F8F8F8",
                    borderRadius: "16px",
                    padding: "16px",
                    marginTop: "16px",
                  }}
                >
                  <div className="total-row">
                    <span className="total-label">Sub Total</span>
                    <span className="total-value">
                      {currencySymbol}
                      {subTotalWithOutTax.toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>

                  {totalTax > 0 && (
                    <div className="total-row-wo-border my-1">
                      <span className="total-label d-inline-flex align-items-center gap-1">
                        GST
                        <InfoTooltip
                          rows={[
                            ["On amount", money(gstTotals.taxableBase)],
                            ...(gstTotals.cgst > 0 ? [["CGST", money(gstTotals.cgst)]] : []),
                            ...(gstTotals.sgst > 0 ? [["SGST", money(gstTotals.sgst)]] : []),
                            ...(gstTotals.igst > 0 ? [["IGST", money(gstTotals.igst)]] : []),
                            ["Total GST", money(totalTax), true],
                          ]}
                        />
                      </span>
                      <span className="total-value">
                        {currencySymbol}
                        {totalTax.toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  )}

                  {/* {returnedSubTotal > 0 && (
                    <div className="total-row">
                      <span className="total-label">Return Total</span>
                      <span className="total-value" style={{ color: "red" }}>
                        - {currencySymbol}
                        {returnedSubTotal.toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  )} */}

                  <div className="total-row-with-input">
                    <span className="total-label d-inline-flex align-items-center gap-1">
                      Discount
                      {hasDiscount && (
                        <InfoTooltip
                          rows={[
                            ["On amount", money(subTotalWithOutTax)],
                            [
                              "Entered",
                              discountType === "%"
                                ? `${toNumber(discount)}%`
                                : money(discount),
                            ],
                            ["Discount amount", `- ${money(discountTotal)}`],
                            ...(items.length > 0
                              ? [[`Per item (÷${items.length})`, `- ${money(perItemDiscount)}`]]
                              : []),
                          ]}
                        />
                      )}
                    </span>
                    <div className="total-input-group">
                      <input
                        type="text"
                        className="form-control discount-input"
                        placeholder={`${currencySymbol} Amount`}
                        value={discount}
                        onChange={(e) =>
                          setDiscount(sanitizeNumber(e.target.value))
                        }
                      />
                      <select
                        className="form-select discount-select"
                        value={discountType}
                        onChange={(e) => setDiscountType(e.target.value)}
                      >
                        <option value="Rs.">Rs.</option>
                        <option value="%">%</option>
                      </select>
                    </div>
                    <span className="discount-value">
                      -{parseFloat(discountTotal.toFixed(2)).toLocaleString("en-IN")}
                    </span>
                  </div>
<div className="total-row-with-input">
                      <span className="total-label d-inline-flex align-items-center gap-1">
                        Adjustment
                        <InfoTooltip
                          rows={[
                            [
                              "Adjustment",
                              `${toNumber(adjustment) < 0 ? "- " : ""}${money(Math.abs(toNumber(adjustment)))}`,
                            ],
                            ["Applied to", "Grand total"],
                          ]}
                        />
                      </span>
                      <input
                        type="text"
                        className="form-control adjustment-input"
                        placeholder="0.00"
                        value={adjustment}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          const val =
                            rawValue === ""
                              ? ""
                              : sanitizeSignedNumericInput(rawValue);
                          setAdjustment(val);
                        }}
                      />
                      <span className="discount-value">{adjustment}</span>
                    </div>
                    <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    {validations.adjust_amount}
                  </span>
                  <div className="total-row total-final">
                    <span className="total-label-final">Total ({currencySymbol})</span>
                    <span className="total-value-final">
                      {currencySymbol}
                      {total.toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>

                  {/* Organization currency differs from the selected currency —
                      show the base-currency equivalent (Total × Exchange Rate). */}
                  {showConversionRate && (
                    <div className="total-row">
                      <span className="total-label d-inline-flex align-items-center gap-1">
                        Total in {orgCurrencyName || "Org Currency"}
                        <InfoTooltip
                          rows={[
                            ["Total Amount", `${currencySymbol}${total.toFixed(2)}`],
                            ["Exchange Rate", `× ${Number(exchangeRate) || 1}`],
                            [
                              "Converted Amount",
                              `${orgCurrencySymbol} ${(total * (Number(exchangeRate) || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                              true,
                            ],
                          ]}
                        />
                      </span>
                      <span className="total-value">
                        {orgCurrencySymbol} {(total * (Number(exchangeRate) || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}



                  <div className="total-row-with-input">
                    <div className="tds-radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="tdsOption"
                          value="TDS"
                          checked={tdsOption === "TDS"}
                          onChange={(e) => setTdsOption(e.target.value)}
                        />
                        <span>TDS</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="tdsOption"
                          value="TCS"
                          checked={tdsOption === "TCS"}
                          onChange={(e) => setTdsOption(e.target.value)}
                        />
                        <span>TCS</span>
                      </label>
                      <InfoTooltip
                        rows={[
                          ["Type", tdsOption],
                          [
                            "On amount",
                            money(toNumber(subTotalWithOutTax) - toNumber(discountTotal)),
                          ],
                          ["Rate", `${toNumber(tdsPercentage)}%`],
                          [
                            tdsOption === "TDS" ? "TDS" : "TCS",
                            `${tdsOption === "TDS" ? "- " : "+ "}${money(tdsCalcValue)}`,
                            true,
                          ],
                        ]}
                      />
                    </div>
                    <input
                      type="number"
                      placeholder="Enter %"
                      className="form-control adjustment-input"
                      value={tdsPercentage || ""}
                      onChange={(e) => {
                        const val = sanitizeNumber(e.target.value, 100);
                        setTdsPercentage(val);
                      }}
                    />
                    <span className="discount-value">
                      {tdsOption === "TDS"
                        ? ` - ${parseFloat(tdsCalcValue.toFixed(2)).toLocaleString("en-IN")}`
                        : ` ${parseFloat(tdsCalcValue.toFixed(2)).toLocaleString("en-IN")}`}
                    </span>
                  </div>
                  <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    {validations.tds_percentage}
                  </span>
                  <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    {validations.tcs_percentage}
                  </span>
                  
                  

                  {/* Receivable = Total ∓ TDS/TCS (TDS subtracts, TCS adds). */}
                  <div className="total-row">
                    <span className="total-label" style={{ fontWeight: 600 }}>Receivable</span>
                    <span className="total-value" style={{ fontWeight: 700 }}>
                      {currencySymbol}
                      {(tdsOption === "TCS" ? total + tdsCalcValue : total - tdsCalcValue).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Add charges Section */}

        {/* Returned Products Section */}
        <div className="col-sm-12  mt-3">
          <div className=" order-card">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title">
                Returned Products : {returnedItems.length}
              </h5>
              <Button
                variant="success"
                size="sm"
                onClick={handleAddReturnedItemsToInvoice}
                disabled={selectedReturnedItems.length === 0}
              >
                Add to Invoice
              </Button>
            </div>

            <div className="items-table-wrapper">
              <table className="items-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}>
                      <input
                        type="checkbox"
                        onChange={handleSelectAllReturnedItems}
                        checked={
                          selectedReturnedItems.length ===
                            returnedItems.length && returnedItems.length > 0
                        }
                      />
                    </th>
                    <th>ITEM NAME</th>
                    <th>QUANTITY</th>
                    {selectedReturnedItems.length > 0 && <th>RETURN QTY</th>}
                    <th>RATE</th>
                    {isCgst && <th>CGST (%)</th>}
                    {isSgst && <th>SGST (%)</th>}
                    {isIgst && <th>IGST (%)</th>}
                    <th>TOTAL AMOUNT</th>
                    {/* <th></th> */}
                  </tr>
                </thead>
                <tbody>
                  {returnedItems.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedReturnedItems.includes(idx)}
                          onChange={() => handleSelectReturnedItem(idx)}
                        />
                      </td>
                      <td>
                        <span className="table-label">
                          {item.itemName || "---"}
                        </span>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="table-input"
                          placeholder="Enter quantity"
                          value={item.quantity}
                          /* onChange={(e) =>
                            updateReturnedItem(idx, "quantity", e.target.value)
                          } */
                          readOnly
                        />
                      </td>
                      {selectedReturnedItems.length > 0 && (
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            placeholder="Enter return quantity"
                            value={item.return_quantity}
                            readOnly={!selectedReturnedItems.includes(idx)}
                            onChange={(e) => {
                              const maxQty = Number(item.quantity) || 0;
                              const nextValue = sanitizeNumber(
                                e.target.value,
                                maxQty,
                              );
                              const nextNumber = Number(nextValue);
                              if (Number.isNaN(nextNumber)) return;
                              if (nextNumber <= maxQty) {
                                updateReturnedItem(
                                  idx,
                                  "return_quantity",
                                  nextValue,
                                );
                              }
                            }}
                          />
                        </td>
                      )}
                      <td>
                        <span className="table-label">
                          {item.rate ? `${currencySymbol} ${item.rate}` : "---"}
                        </span>
                      </td>
                      {isCgst && (
                        <td>
                          <span className="table-label">
                            {item.cgst !== undefined && item.cgst !== null && item.cgst !== ""
                              ? `${Number(item.cgst) > 100 ? Number(item.cgst) / 10 : Number(item.cgst)}%`
                              : "---"}
                          </span>
                        </td>
                      )}
                      {isSgst && (
                        <td>
                          <span className="table-label">
                            {item.sgst !== undefined && item.sgst !== null && item.sgst !== ""
                              ? `${Number(item.sgst) > 100 ? Number(item.sgst) / 10 : Number(item.sgst)}%`
                              : "---"}
                          </span>
                        </td>
                      )}
                      {isIgst && (
                        <td>
                          <span className="table-label">
                            {item.igst !== undefined && item.igst !== null && item.igst !== ""
                              ? `${Number(item.igst) > 100 ? Number(item.igst) / 10 : Number(item.igst)}%`
                              : "---"}
                          </span>
                        </td>
                      )}

                      <td>
                        <span className="table-label">
                          {item.totalAmount ? `${currencySymbol} ${item.totalAmount}` : "---"}
                        </span>
                      </td>
                      {/*  <td>
                        {returnedItems.length > 1 && (
                          <button
                            className="delete-row-btn"
                            onClick={() => removeReturnedItem(idx)}
                            type="button"
                          >
                            <img
                              src={Delete}
                              alt="delete"
                              className="delete-icon"
                            />
                          </button>
                        )}
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default InvoiceCreate;
